import { Collection, Database, Q } from '@nozbe/watermelondb';
import {
    OnboardingModel,
    OnboardingPayload,
    UserOnboardingPayload,
} from '../../types/RsvpOnboarding';
import { OnboardingServiceOptions } from '../../types/dbService';
import {
    addPreparedDataBatch,
    mapRecord,
    PreparedOnboardingData,
} from './helpers/onboarding/common';
import { validateEntityRecords } from './helpers/common/validateEntityRecords';
import {
    logAddOnboardingAction,
    prepareCreateOnboarding,
} from './helpers/onboarding/onboardingAdd';
import {
    logUpdateOnboardingAction,
    prepareUpdateOnboarding,
    prepareUpdateOnboardingUser,
} from './helpers/onboarding/onboardingUpdate';

import { UserModel } from '../../types/User';

class RsvpOnboarding {
    private database: Database;
    private collection: Collection<OnboardingModel>;
    private table = 'onboardings';
    private options: OnboardingServiceOptions;

    constructor(options: OnboardingServiceOptions) {
        this.database = options.database;
        this.collection = options.database.collections.get(this.table);
        this.options = options;
    }

    getByUserId(userId: string) {
        return this.collection.query(Q.where('user_id', userId));
    }

    getAll() {
        return this.collection.query();
    }

    async add(payload: OnboardingPayload) {
        return this.database.write(async () => {
            const preparedData: PreparedOnboardingData[] = [];
            const createdOnboarding = prepareCreateOnboarding({
                payload,
                collection: this.collection,
            });
            preparedData.push(
                mapRecord(createdOnboarding, 'onboardings', 'add'),
            );

            validateEntityRecords(
                preparedData,
                this.options.validation,
            );
            await addPreparedDataBatch(preparedData, this.database);

            logAddOnboardingAction({
                onboarding: createdOnboarding,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });

            return createdOnboarding;
        });
    }

    async update({
        payload,
        rsvpOnboardingId,
        userOnboardingPayload,
    }: {
        payload: OnboardingPayload;
        rsvpOnboardingId: string;
        userOnboardingPayload?: UserOnboardingPayload;
    }) {
        const rsvpOnboarding = await this.collection.find(rsvpOnboardingId);

        return this.database.write(async () => {
            const preparedData: PreparedOnboardingData[] = [];
            const updatedRsvpOnboarding = prepareUpdateOnboarding({
                onboardingElement: rsvpOnboarding,
                payload,
            });
            preparedData.push(
                mapRecord(updatedRsvpOnboarding, 'onboardings', 'update'),
            );

            let updatedUser: UserModel | undefined;
            if (userOnboardingPayload) {
                const user = await this.database.collections
                    .get<UserModel>('users')
                    .find(payload.userId);
                updatedUser = prepareUpdateOnboardingUser({
                    user,
                    payload: userOnboardingPayload,
                });
                preparedData.push(mapRecord(updatedUser, 'users', 'update'));
            }

            validateEntityRecords(
                preparedData,
                this.options.validation,
            );
            await addPreparedDataBatch(preparedData, this.database);

            logUpdateOnboardingAction({
                onboarding: updatedRsvpOnboarding,
                user: updatedUser,
                logDBAction: this.options.logger.logDBAction,
                tableName: this.table,
            });

            return updatedRsvpOnboarding;
        });
    }
}

export default RsvpOnboarding;
